import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useParams } from 'react-router-dom';

export default function PublicForm() {
  const { slug } = useParams();
  const [addressBook, setAddressBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    birthMonth: '',
    birthDay: '',
    hasSignificantOther: false,
    soFirstName: '',
    soLastName: '',
    soPhone: '',
    soBirthMonth: '',
    soBirthDay: ''
  });

  // Helper function to convert month name to number
  function getMonthNumber(monthName) {
    const months = {
      'January': '01', 'February': '02', 'March': '03', 'April': '04',
      'May': '05', 'June': '06', 'July': '07', 'August': '08',
      'September': '09', 'October': '10', 'November': '11', 'December': '12'
    };
    return months[monthName] || '01';
  }

  // Helper function to convert birthday to database format (YYYY-MM-DD)
  function formatBirthdayForDB(month, day) {
    if (!month || !day) return null;
    const monthNum = getMonthNumber(month);
    const dayNum = String(day).padStart(2, '0');
    return `2024-${monthNum}-${dayNum}`;
  }

  useEffect(() => {
    loadAddressBook();
  }, [slug]);

  async function loadAddressBook() {
    try {
      const { data, error } = await supabase
        .from('address_books')
        .select('id, title, owner_id')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) throw error;

      if (!data) {
        setAddressBook(null);
      } else {
        setAddressBook(data);
      }
    } catch (error) {
      console.error('Error loading address book:', error);
      setAddressBook(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      alert('Please enter at least your first and last name');
      return;
    }

    setSubmitting(true);

    try {
      const birthday = formatBirthdayForDB(formData.birthMonth, formData.birthDay);
      const soBirthday = formData.hasSignificantOther 
        ? formatBirthdayForDB(formData.soBirthMonth, formData.soBirthDay)
        : null;

      const significantOther = formData.hasSignificantOther && (formData.soFirstName || formData.soLastName)
        ? `${formData.soFirstName} ${formData.soLastName}`.trim()
        : null;

      const { error } = await supabase
        .from('contacts')
        .insert({
          address_book_id: addressBook.id,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email || null,
          phone: formData.phone || null,
          street: formData.street || null,
          city: formData.city || null,
          state: formData.state || null,
          zip_code: formData.zip || null,
          birthday: birthday,
          so_name: significantOther,
          so_phone: formData.hasSignificantOther ? (formData.soPhone || null) : null,
          so_birthday: soBirthday,
          submitted_at: new Date().toISOString()
        });

      if (error) throw error;

      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting:', error);
      alert('There was an error submitting your information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ fontSize: '18px', color: 'white', fontWeight: '500' }}>Loading...</div>
      </div>
    );
  }

  if (!addressBook) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '20px' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '16px', textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📪</div>
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1e293b', marginBottom: '10px' }}>
            Address Book Not Found
          </h2>
          <p style={{ color: '#64748b', fontSize: '15px' }}>
            This link may be inactive or incorrect.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '20px' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '16px', textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
          <h2 style={{ fontSize: '28px', fontWeight: '600', color: '#1e293b', marginBottom: '15px' }}>
            Thank You!
          </h2>
          <p style={{ color: '#64748b', fontSize: '16px', lineHeight: '1.6' }}>
            Your information has been submitted successfully.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '20px', fontFamily: 'Poppins, sans-serif' }}>
      <div style={{ maxWidth: '500px', margin: '0 auto', paddingTop: '20px', paddingBottom: '40px' }}>
        
        <div style={{ background: 'rgba(255,255,255,0.95)', padding: '20px', borderRadius: '12px', marginBottom: '20px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '16px', color: '#1e293b', marginBottom: '12px', fontWeight: '500' }}>
            Want to collect addresses from your friends and family?
          </div>
          <a 
            href="https://postalgather.com" 
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-block', padding: '12px 24px', background: '#10b981', color: 'white', textDecoration: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', transition: 'all 0.2s' }}
            onMouseOver={(e) => e.target.style.background = '#059669'}
            onMouseOut={(e) => e.target.style.background = '#10b981'}
          >
            Sign Up For Your Own Account Here →
          </a>
        </div>

        <div style={{ background: 'white', padding: '30px', borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '8px', textAlign: 'center' }}>
            📬 Share Your Address
          </h1>
          <p style={{ color: '#64748b', fontSize: '15px', textAlign: 'center', marginBottom: '25px', lineHeight: '1.5' }}>
            Help us stay in touch by sharing your contact information
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <input
                type="text"
                placeholder="First Name *"
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                required
                style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}
              />
              <input
                type="text"
                placeholder="Last Name *"
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                required
                style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <input
                type="tel"
                placeholder="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}
              />
            </div>

            <div style={{ marginBottom: '15px', padding: '15px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '16px' }}>🎂</span>
                <span>Birthday</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px' }}>
                <select
                  value={formData.birthMonth}
                  onChange={(e) => setFormData({...formData, birthMonth: e.target.value})}
                  style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: 'white', fontFamily: 'Poppins, sans-serif' }}
                >
                  <option value="">Month</option>
                  <option value="January">January</option>
                  <option value="February">February</option>
                  <option value="March">March</option>
                  <option value="April">April</option>
                  <option value="May">May</option>
                  <option value="June">June</option>
                  <option value="July">July</option>
                  <option value="August">August</option>
                  <option value="September">September</option>
                  <option value="October">October</option>
                  <option value="November">November</option>
                  <option value="December">December</option>
                </select>
                <select
                  value={formData.birthDay}
                  onChange={(e) => setFormData({...formData, birthDay: e.target.value})}
                  style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: 'white', fontFamily: 'Poppins, sans-serif' }}
                >
                  <option value="">Day</option>
                  {[...Array(31)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '15px', padding: '15px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '16px' }}>💑</span>
                <span>Significant Other</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: formData.hasSignificantOther ? '12px' : '0' }}>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, hasSignificantOther: true})}
                  style={{ flex: 1, padding: '10px', background: formData.hasSignificantOther ? '#10b981' : 'white', color: formData.hasSignificantOther ? 'white' : '#64748b', border: '2px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', fontFamily: 'Poppins, sans-serif', transition: 'all 0.2s' }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, hasSignificantOther: false, soFirstName: '', soLastName: '', soPhone: '', soBirthMonth: '', soBirthDay: ''})}
                  style={{ flex: 1, padding: '10px', background: !formData.hasSignificantOther ? '#ef4444' : 'white', color: !formData.hasSignificantOther ? 'white' : '#64748b', border: '2px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', fontFamily: 'Poppins, sans-serif', transition: 'all 0.2s' }}
                >
                  No
                </button>
              </div>
              
              {formData.hasSignificantOther && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      placeholder="First Name"
                      value={formData.soFirstName}
                      onChange={(e) => setFormData({...formData, soFirstName: e.target.value})}
                      style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif', background: 'white' }}
                    />
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={formData.soLastName}
                      onChange={(e) => setFormData({...formData, soLastName: e.target.value})}
                      style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif', background: 'white' }}
                    />
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <input
                      type="tel"
                      placeholder="Their Phone"
                      value={formData.soPhone}
                      onChange={(e) => setFormData({...formData, soPhone: e.target.value})}
                      style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif', background: 'white' }}
                    />
                  </div>
                  
                  <div style={{ paddingTop: '10px', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: '#64748b', marginBottom: '8px' }}>
                      Their Birthday
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px' }}>
                      <select
                        value={formData.soBirthMonth}
                        onChange={(e) => setFormData({...formData, soBirthMonth: e.target.value})}
                        style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: 'white', fontFamily: 'Poppins, sans-serif' }}
                      >
                        <option value="">Month</option>
                        <option value="January">January</option>
                        <option value="February">February</option>
                        <option value="March">March</option>
                        <option value="April">April</option>
                        <option value="May">May</option>
                        <option value="June">June</option>
                        <option value="July">July</option>
                        <option value="August">August</option>
                        <option value="September">September</option>
                        <option value="October">October</option>
                        <option value="November">November</option>
                        <option value="December">December</option>
                      </select>
                      <select
                        value={formData.soBirthDay}
                        onChange={(e) => setFormData({...formData, soBirthDay: e.target.value})}
                        style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: 'white', fontFamily: 'Poppins, sans-serif' }}
                      >
                        <option value="">Day</option>
                        {[...Array(31)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div style={{ marginBottom: '10px' }}>
              <input
                type="text"
                placeholder="Street Address"
                value={formData.street}
                onChange={(e) => setFormData({...formData, street: e.target.value})}
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <input
                type="text"
                placeholder="City"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
              <input
                type="text"
                placeholder="State"
                value={formData.state}
                onChange={(e) => setFormData({...formData, state: e.target.value.toUpperCase().slice(0, 2)})}
                maxLength="2"
                style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif', textTransform: 'uppercase' }}
              />
              <input
                type="text"
                placeholder="ZIP"
                value={formData.zip}
                onChange={(e) => setFormData({...formData, zip: e.target.value})}
                style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{ width: '100%', padding: '14px', background: submitting ? '#94a3b8' : '#667eea', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'Poppins, sans-serif' }}
            >
              {submitting ? 'Submitting...' : 'Submit My Information'}
            </button>
          </form>
        </div>

        <div style={{ textAlign: 'center', marginTop: '20px', color: 'white', fontSize: '13px', opacity: 0.8 }}>
          Powered by PostalGather
        </div>
      </div>
    </div>
  );
}
